import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { fetchJSON } from '../utils/api';

export default function ProtectedRoute() {
  const location = useLocation();
  const [state, setState] = useState({
    loading: true,
    authenticated: false,
    unavailable: false,
  });

  const checkSession = useCallback((active = () => true) => {
    setState((current) => ({
      ...current,
      loading: true,
      unavailable: false,
    }));

    return fetchJSON('/api/auth/session')
      .then(() => {
        if (active()) {
          setState({ loading: false, authenticated: true, unavailable: false });
        }
      })
      .catch((err) => {
        if (!active()) return;

        const status = Number(err?.status || 0);
        const shouldRedirect = status === 401 || status === 403;

        setState({
          loading: false,
          authenticated: false,
          unavailable: !shouldRedirect,
        });
      });
  }, []);

  useEffect(() => {
    let mounted = true;

    checkSession(() => mounted);

    return () => {
      mounted = false;
    };
  }, [checkSession]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-700">
        <div className="rounded-lg border border-slate-200 bg-white px-5 py-4 text-sm shadow-sm">
          Checking session...
        </div>
      </div>
    );
  }

  if (!state.authenticated) {
    if (state.unavailable) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 text-slate-700">
          <div className="max-w-md rounded-2xl border border-slate-200 bg-white px-6 py-5 text-center text-sm shadow-sm">
            <div className="text-base font-semibold text-slate-900">
              Could not verify your session
            </div>
            <p className="mt-2 leading-6 text-slate-600">
              InboxOps could not reach the session service. Your login may still be valid, so try again before signing in.
            </p>
            <button
              type="button"
              className="mt-4 inline-flex h-10 items-center justify-center rounded-xl bg-blue-600 px-4 font-semibold text-white"
              onClick={() => checkSession()}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}
